/**
 * Screen-space overlay pass (engine 46.3): the cell grid, the hovered-cell
 * cursor / stamp footprint, the live rect marquee (Rect tool + Picker
 * rect-drag), and an optional chunk-debug view. Drawn after all layers.
 */

import { CHUNK_SIZE } from '../model/constants';
import { type Camera, worldToScreen } from './camera';

const BRAND_ORANGE = '#F5793B'; // engine editor accent (46.2/46.3)

export interface OverlayOptions {
    showGrid: boolean;
    showChunkDebug: boolean;
    cellSize: number;
    hoveredCell: { x: number; y: number } | null;
    /** Footprint of the active stamp (cells). 1×1 for a single-tile brush. */
    stampW: number;
    stampH: number;
    brushSize: number;
    /** Live drag rectangle in cell coords (rect/pick), or null. */
    dragRect: { start: { x: number; y: number }; end: { x: number; y: number } } | null;
    /**
     * Collision preview: world-space rects (already chunk-offset) to draw as
     * translucent green boxes (engine 47.1's debug overlay, _docs/tilemap/05 §7).
     */
    collisionRects: Array<{ x: number; y: number; w: number; h: number }>;
}

export function drawOverlay(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    cssW: number,
    cssH: number,
    opts: OverlayOptions,
): void {
    if (opts.showGrid) drawGrid(ctx, cam, cssW, cssH, opts.cellSize);
    if (opts.showChunkDebug) drawChunkDebug(ctx, cam, cssW, cssH, opts.cellSize);
    if (opts.collisionRects.length) drawCollisionRects(ctx, cam, cssH, opts.cellSize, opts.collisionRects);
    if (opts.dragRect) {
        drawCellRect(ctx, cam, cssH, opts.cellSize, opts.dragRect.start, opts.dragRect.end);
    } else if (opts.hoveredCell) {
        drawStampCursor(ctx, cam, cssH, opts.cellSize, opts.hoveredCell, opts.stampW, opts.stampH, opts.brushSize);
    }
}

function drawGrid(ctx: CanvasRenderingContext2D, cam: Camera, cssW: number, cssH: number, cellSize: number): void {
    const px = cellSize * cam.zoom;
    if (px < 6) return;

    const c0 = Math.floor(cam.x / cellSize);
    const c1 = Math.ceil((cam.x + cssW / cam.zoom) / cellSize);
    const r0 = Math.floor(cam.y / cellSize);
    const r1 = Math.ceil((cam.y + cssH / cam.zoom) / cellSize);

    ctx.save();
    ctx.lineWidth = 1;
    for (let cx = c0; cx <= c1; cx++) {
        const s = worldToScreen(cam, cssH, cx * cellSize, 0);
        ctx.strokeStyle = cx % CHUNK_SIZE === 0 ? 'rgba(128,128,128,0.45)' : 'rgba(128,128,128,0.15)';
        line(ctx, Math.round(s.x) + 0.5, 0, Math.round(s.x) + 0.5, cssH);
    }
    for (let cy = r0; cy <= r1; cy++) {
        const s = worldToScreen(cam, cssH, 0, cy * cellSize);
        ctx.strokeStyle = cy % CHUNK_SIZE === 0 ? 'rgba(128,128,128,0.45)' : 'rgba(128,128,128,0.15)';
        line(ctx, 0, Math.round(s.y) + 0.5, cssW, Math.round(s.y) + 0.5);
    }
    ctx.restore();
}

function drawChunkDebug(ctx: CanvasRenderingContext2D, cam: Camera, cssW: number, cssH: number, cellSize: number): void {
    const chunkWorld = cellSize * CHUNK_SIZE;
    const c0 = Math.floor(cam.x / chunkWorld) - 1;
    const c1 = Math.ceil((cam.x + cssW / cam.zoom) / chunkWorld) + 1;
    const r0 = Math.floor(cam.y / chunkWorld) - 1;
    const r1 = Math.ceil((cam.y + cssH / cam.zoom) / chunkWorld) + 1;

    ctx.save();
    ctx.strokeStyle = 'rgba(60,160,255,0.6)';
    ctx.fillStyle = 'rgba(60,160,255,0.8)';
    ctx.font = '10px monospace';
    ctx.lineWidth = 1;
    for (let cy = r0; cy <= r1; cy++) {
        for (let cx = c0; cx <= c1; cx++) {
            const tl = worldToScreen(cam, cssH, cx * chunkWorld, (cy + 1) * chunkWorld);
            const size = chunkWorld * cam.zoom;
            ctx.strokeRect(Math.round(tl.x) + 0.5, Math.round(tl.y) + 0.5, size, size);
            ctx.fillText(`${cx},${cy}`, tl.x + 3, tl.y + 12);
        }
    }
    ctx.restore();
}

function drawStampCursor(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    cssH: number,
    cellSize: number,
    cell: { x: number; y: number },
    stampW: number,
    stampH: number,
    brushSize: number,
): void {
    // Single-tile stamp uses the N×N brush block centered on the cursor;
    // a multi-tile stamp shows its footprint anchored top-left at the cursor.
    const single = stampW === 1 && stampH === 1;
    const w = single ? brushSize : stampW;
    const h = single ? brushSize : stampH;
    const originX = single ? cell.x - Math.floor(brushSize / 2) : cell.x;
    const topY = single ? cell.y - Math.floor(brushSize / 2) + h : cell.y + 1; // top edge world Y

    const topLeft = worldToScreen(cam, cssH, originX * cellSize, topY * cellSize);
    ctx.save();
    ctx.strokeStyle = BRAND_ORANGE;
    ctx.lineWidth = 2;
    ctx.strokeRect(Math.round(topLeft.x) + 0.5, Math.round(topLeft.y) + 0.5, w * cellSize * cam.zoom, h * cellSize * cam.zoom);
    ctx.restore();
}

/** Merged collider rects, translucent green — engine 47.1's debug overlay. */
function drawCollisionRects(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    cssH: number,
    cellSize: number,
    rects: Array<{ x: number; y: number; w: number; h: number }>,
): void {
    ctx.save();
    ctx.fillStyle = 'rgba(40,200,90,0.28)';
    ctx.strokeStyle = 'rgba(40,200,90,0.9)';
    ctx.lineWidth = 1.5;
    for (const r of rects) {
        const topLeft = worldToScreen(cam, cssH, r.x * cellSize, (r.y + r.h) * cellSize);
        const w = r.w * cellSize * cam.zoom;
        const h = r.h * cellSize * cam.zoom;
        ctx.fillRect(topLeft.x, topLeft.y, w, h);
        ctx.strokeRect(Math.round(topLeft.x) + 0.5, Math.round(topLeft.y) + 0.5, w, h);
    }
    ctx.restore();
}

function drawCellRect(
    ctx: CanvasRenderingContext2D,
    cam: Camera,
    cssH: number,
    cellSize: number,
    start: { x: number; y: number },
    end: { x: number; y: number },
): void {
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    const topLeft = worldToScreen(cam, cssH, minX * cellSize, (maxY + 1) * cellSize);
    const w = (maxX - minX + 1) * cellSize * cam.zoom;
    const h = (maxY - minY + 1) * cellSize * cam.zoom;

    ctx.save();
    ctx.fillStyle = 'rgba(245,121,59,0.15)';
    ctx.strokeStyle = BRAND_ORANGE;
    ctx.lineWidth = 2;
    ctx.fillRect(topLeft.x, topLeft.y, w, h);
    ctx.strokeRect(Math.round(topLeft.x) + 0.5, Math.round(topLeft.y) + 0.5, w, h);
    ctx.restore();
}

function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number): void {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
}
